import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import VillageHero from '../components/VillageHero';
import VillageNewsGrid from '../components/VillageNewsGrid';
import VillageVideoSection from '../components/VillageVideoSection';
import VillageGallery from '../components/VillageGallery';
import { getVillageData } from '../data/villageDummyData';
import { buildApiUrl } from '../utils/api/config';
import '../styles/villagePage.css';

function VillagePage() {
  const { villageName } = useParams();
  const decodedVillageName = villageName ? decodeURIComponent(villageName) : '';
  const [villageData, setVillageData] = useState(null);
  const [apiNews, setApiNews] = useState([]);

  const extractYouTubeId = (url) => {
    if (!url) {
      return '';
    }

    const normalizedUrl = String(url).trim();
    const match = normalizedUrl.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^#&?/]{11})/
    );

    return match ? match[1] : '';
  };

  const getYouTubeThumbnail = (url, fallbackImage = '') => {
    const youtubeId = extractYouTubeId(url);

    if (youtubeId) {
      return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
    }

    return fallbackImage;
  };

  const normalizeLocationLabel = (value, placeholder) => {
    if (!value) {
      return '';
    }

    const normalizedValue = String(value).trim();
    return normalizedValue === placeholder ? '' : normalizedValue;
  };

  // Fetch village data and API news on mount
  useEffect(() => {
    // Reset state when village changes
    setVillageData(null);
    setApiNews([]);

    const fetchData = async () => {
      try {
        console.log('🔍 VillagePage: Fetching data for village:', decodedVillageName);
        
        // Get village from database by name
        const villageResponse = await fetch(
          buildApiUrl(`/locations/villages?name=${encodeURIComponent(decodedVillageName)}`)
        );
        const villageResult = await villageResponse.json();

        console.log('📍 VillagePage: Village API Response:', villageResult);

        if (villageResult.success && villageResult.data.length > 0) {
          const village = villageResult.data[0];
          console.log('✅ VillagePage: Found village:', village.name, 'with ID:', village._id);
          setVillageData(village);

          // Get news for this village from API using village ID
          const newsResponse = await fetch(
            buildApiUrl(`/news/village/${village._id}?limit=100`)
          );
          const newsResult = await newsResponse.json();

          console.log('📰 VillagePage: News API Response:', newsResult);

          if (newsResult.success) {
            console.log('✅ VillagePage: Found', newsResult.data.length, 'news items for village');
            setApiNews(newsResult.data);
          } else {
            console.log('⚠️ VillagePage: News API returned success=false');
            setApiNews([]);
          }
        } else {
          console.log('❌ VillagePage: No village found, using fallback data');
          setVillageData(null);
          setApiNews([]);
        }
      } catch (error) {
        console.error('❌ VillagePage: Error fetching data from API:', error);
        setVillageData(null);
        setApiNews([]);
      }
    };

    if (decodedVillageName) {
      fetchData();
    }
  }, [decodedVillageName]);

  // Get dummy data as fallback or get actual village data if existing
  const dummyData = getVillageData(decodedVillageName);
  const resolvedBlockName =
    normalizeLocationLabel(villageData?.blockName, 'ब्लॉक') ||
    normalizeLocationLabel(dummyData.blockName, 'ब्लॉक');

  const resolvedDistrictName =
    normalizeLocationLabel(villageData?.districtName, 'जिला') ||
    normalizeLocationLabel(dummyData.districtName, 'जिला');

  const displayData = {
    ...dummyData,
    blockName: resolvedBlockName,
    districtName: resolvedDistrictName
  };

  const villageIntro = displayData.blockName
    ? `${displayData.blockName} के अंतर्गत आने वाला एक महत्वपूर्ण गांव है।`
    : 'एक महत्वपूर्ण गांव है।';

  // If we have API news, replace dummy news with API news
  if (apiNews.length > 0) {
    console.log(' VillagePage: Mapping', apiNews.length, 'news items');
    displayData.news = apiNews.map(item => ({
      id: item._id,
      headline: item.title,
      description: item.shortDescription,
      image: item.image,
      date: new Date(item.createdAt).toLocaleDateString('hi-IN'),
      category: item.category
    }));

    const apiVideos = apiNews
      .filter(item => item.videoUrl)
      .map(item => {
        const youtubeId = extractYouTubeId(item.videoUrl);

        if (!youtubeId) {
          return null;
        }

        return {
          id: `${item._id}-video`,
          title: item.title,
          description: item.shortDescription,
          youtubeId,
          thumbnail: getYouTubeThumbnail(item.videoUrl, item.image || item.thumbnail || '')
        };
      })
      .filter(Boolean);

    if (apiVideos.length > 0) {
      displayData.videos = apiVideos;
      console.log('✅ VillagePage: Mapped videos:', displayData.videos.length, 'items');
    } else {
      console.log('⚠️ VillagePage: No API videos found, using dummy video data');
    }

    console.log('✅ VillagePage: Mapped news:', displayData.news.length, 'items');
  } else {
    console.log('⚠️ VillagePage: No API news, using dummy data');
  }

  // If no village name in URL
  if (!decodedVillageName) {
    return (
      <div className="village-page">
        <div style={{padding: '60px 20px', textAlign: 'center'}}>
          <h1 style={{color: 'var(--primary)', fontSize: '2rem'}}>खेद है!</h1>
          <p style={{fontSize: '1.1rem', color: 'var(--text-secondary)'}}>
            कृपया नेविगेशन मेनू से कोई गांव चुनें।
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="village-page">
      <div className="village-page-container">
        {/* Hero Section */}
        <VillageHero
          villageName={decodedVillageName}
          blockName={displayData.blockName}
          districtName={displayData.districtName}
        />

        {/* Featured Article */}
        <div className="featured-article">
          <div className="featured-image">
            <img src={displayData.featured.image} alt={displayData.featured.headline} />
          </div>
          <div className="featured-content">
            <h2 className="featured-headline"> {displayData.featured.headline}</h2>
            <p className="featured-description">{displayData.featured.description}</p>
            <div className="featured-meta">
              <span> {displayData.featured.date}</span>
              <span>{displayData.featured.reporter}</span>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="village-content">
          {/* Left Side - Main Content */}
          <div>
            {/* News Grid */}
            <VillageNewsGrid news={displayData.news} villageName={decodedVillageName} />

            {/* Article Text Section */}
            <div className="article-text-section">
              <h2 className="section-title">गांव के बारे में जानकारी</h2>
              <div className="article-text">
                <p>
                  <strong>{decodedVillageName}</strong>, {villageIntro} यह गांव {displayData.districtName} जिले के विकास में महत्वपूर्ण भूमिका निभाता है।
                </p>
                <p>
                  {displayData.description} यहाँ की जनता मेहनती और समर्पित है। गांव के विकास के लिए स्थानीय प्रशासन और जनता एक साथ काम कर रहे हैं।
                </p>
                <p>
                  गांव में कृषि मुख्य व्यवसाय है। यहाँ के किसान आधुनिक खेती की तकनीकें अपना रहे हैं। साथ ही, शिक्षा, स्वास्थ्य और बुनियादी ढांचे के विकास में भी सुधार देखा जा रहा है।
                </p>
                <p>
                  सरकार और गैर-सरकारी संस्थाएं गांव के समग्र विकास के लिए विभिन्न योजनाएं लागू कर रहे हैं। युवाओं को शिक्षा और कौशल प्रशिक्षण दिया जा रहा है ताकि वे रोजगार के नए अवसर पा सकें।
                </p>
                <p>
                  गांव की परंपरा और संस्कृति को भी संरक्षित किया जा रहा है। स्थानीय कलाओं और शिल्पों को बढ़ावा दिया जा रहा है जो पर्यटन और रोजगार के नए स्रोत बन सकते हैं।
                </p>
              </div>
            </div>

            {/* Video Section */}
            <VillageVideoSection videos={displayData.videos} />

            {/* Gallery Section */}
            <VillageGallery gallery={displayData.gallery} />

            {/* More from Block */}
            <div className="more-from-block">
              <h2 className="section-title">
                 {displayData.blockName ? `${displayData.blockName} से और पढ़ें` : 'आसपास की और खबरें'}
              </h2>
              <div className="block-news-grid">
                {displayData.blockNews.map((item, index) => (
                  <a
                    key={index}
                    href={item.link}
                    style={{textDecoration: 'none'}}
                  >
                    <div className="block-news-item">
                      <div className="block-news-headline">{item.headline}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <aside className="sidebar">
            {/* Related News Widget */}
            <div className="sidebar-widget">
              <h3 className="sidebar-title">ट्रेंडिंग न्यूज़</h3>
              <div>
                {displayData.relatedNews.map((item, index) => (
                  <div key={index} className="related-item">
                    <div className="related-headline">{item.headline}</div>
                    <div className="related-views"> {item.views} views</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Block Information Widget */}
            <div className="sidebar-widget">
              <h3 className="sidebar-title">गांव की जानकारी</h3>
              <div style={{fontSize: '0.95rem', lineHeight: '1.8', color: '#666'}}>
                <p><strong> गांव:</strong> {decodedVillageName}</p>
                {displayData.blockName && <p><strong>ब्लॉक:</strong> {displayData.blockName}</p>}
                {displayData.districtName && <p><strong>जिला:</strong> {displayData.districtName}</p>}
                <p><strong> कुल खबरें:</strong> {displayData.news.length}</p>
                <p><strong>वीडियो:</strong> {displayData.videos.length}</p>
                <p><strong>फोटो:</strong> {displayData.gallery.length}</p>
              </div>
            </div>

            {/* Subscribe Widget */}
            <div className="sidebar-widget">
              <h3 className="sidebar-title">📬 न्यूज़लेटर</h3>
              <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '12px'}}>
                {decodedVillageName} की सभी खबरें सबसे पहले पाएं।
              </p>
              <input
                type="email"
                placeholder="ईमेल दर्ज करें"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginBottom: '10px',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />
              <button
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#d32f2f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#b71c1c'}
                onMouseLeave={(e) => e.target.style.background = '#d32f2f'}
              >
                सब्सक्राइब करें
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default VillagePage;
